import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Device } from '../../types/Device';
import CameraMarkerIcon from '../icons/CameraMarkerIcon';
import './style/MapAll.css';
import ReactDOMServer from 'react-dom/server';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useNavigate } from 'react-router-dom';
import { MAP_TILE_URL } from '../../const/const';
import { isPointInKazakhstan } from '../../utils/kazakhstanBounds';

interface LocationMapAllProps {
    devices: Device[];
    selectedDeviceUID?: string;
    onSelectDeviceUID?: (uid: string | undefined) => void;
}

function filterDevicesWithGeo(devices: Device[]) {
    return devices.filter((d) =>
        d.latitude !== 0 &&
        d.longitude !== 0 &&
        isPointInKazakhstan(d.latitude, d.longitude)
    );
}

function getBounds(devices: Device[]): [[number, number], [number, number]] | null {
    if (devices.length === 0) return null;
    const lats = devices.map((d) => d.latitude);
    const lngs = devices.map((d) => d.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    return [
        [minLat, minLng],
        [maxLat, maxLng],
    ];
}

const createCameraIcon = (online: boolean, isSelected: boolean, name: string) => {
    const svgString = ReactDOMServer.renderToString(
        <CameraMarkerIcon online={online} isSelected={isSelected} />
    );
    const bgColor = '#fff';
    const textColor = '#575655';
    return L.divIcon({
        className: 'camera-marker-icon',
        html: `<div style="display:flex;align-items:center;width:auto;height:40px;">
            ${svgString}
            <div style="background:${bgColor};color:${textColor};font-family: Roboto; border-radius:0 6px 6px 0;padding:1px 10px 1px 27px;font-size:13px;font-weight:600;white-space:nowrap;line-height:1.2;margin-left:-20px;display:flex;align-items:center;height:26px;border:2px solid #fff;border-left:none;">${name}</div>
        </div>`,
        iconSize: [name.length * 8 + 48, 40],
        iconAnchor: [20, 20],
        popupAnchor: [0, -20],
    });
};

const FitBounds: React.FC<{ bounds: [[number, number], [number, number]] }> = ({ bounds }) => {
    const map = useMap();
    useEffect(() => {
        map.fitBounds(bounds, { padding: [40, 40] });
    }, [bounds, map]);
    return null;
};

const ZoomToDevice: React.FC<{ device: Device | undefined }> = ({ device }) => {
    const map = useMap();
    useEffect(() => {
        if (device) {
            const currentZoom = map.getZoom();
            if (currentZoom < 15) {
                map.flyTo([device.latitude, device.longitude], 16, { animate: true, duration: 0.7 });
            }
        }
    }, [device, map]);
    return null;
};

const LocationMapAll: React.FC<LocationMapAllProps> = ({ devices, selectedDeviceUID, onSelectDeviceUID }) => {
    const mapRef = useRef<L.Map | null>(null);
    const markerRefs = useRef<{ [uid: string]: L.Marker | undefined }>({});
    const [selectedUID, setSelectedUID] = useState<string | undefined>(selectedDeviceUID);
    const [isExternalSelection, setIsExternalSelection] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setSelectedUID(selectedDeviceUID);
        setIsExternalSelection(true);
        if (!selectedDeviceUID && mapRef.current) {
            mapRef.current.closePopup && mapRef.current.closePopup();
        }
    }, [selectedDeviceUID]);

    useEffect(() => {
        return () => {
            markerRefs.current = {};
        };
    }, []);

    const openMarkerClustersAndMarkerPopup = (markerId: string) => {
        const markerElementRef = markerRefs.current[markerId];

        if (!markerElementRef) return;

        if ((markerElementRef as any)._icon) {
            (markerElementRef as any)._icon.click();
        } else {
            const clusters = [];
            let _currentElement = markerElementRef as any;
            while (_currentElement && _currentElement.__parent) {
                clusters.push(_currentElement);
                if (_currentElement._icon) break;
                _currentElement = _currentElement.__parent;
            }

            for (let i = clusters.length - 1; i >= 0; i--) {
                const _icon = clusters[i]._icon;
                if (_icon) {
                    _icon.click();
                }
            }
        }
    };

    useEffect(() => {
        if (!selectedUID) return;
        const map = mapRef.current;
        const marker = markerRefs.current[selectedUID];
        if (map && marker && marker.getLatLng) {
            map.closePopup();
            const currentZoom = map.getZoom();
            const mapBounds = map.getBounds();
            const markerLatLng = marker.getLatLng();
            const isMarkerVisible = mapBounds.contains(markerLatLng);

            if (currentZoom < 15 || !isMarkerVisible) {
                map.flyTo(marker.getLatLng(), 16, { animate: true, duration: 0.7 });
            }

            if (isExternalSelection) {
                setTimeout(() => {
                    const currentZoom = map.getZoom();
                    if (currentZoom < 16) {
                        map.flyTo(marker.getLatLng(), 16, { animate: true, duration: 0.5 });
                        setTimeout(() => {
                            openMarkerClustersAndMarkerPopup(selectedUID);
                        }, 600);
                    } else {
                        openMarkerClustersAndMarkerPopup(selectedUID);
                    }
                }, 300);
                setIsExternalSelection(false);
            }
        }
    }, [selectedUID, isExternalSelection]);

    const validDevices = filterDevicesWithGeo(devices);
    const bounds = getBounds(validDevices);
    const selectedDevice = validDevices.find((d) => d.UID === selectedUID);

    const createClusterIcon = (cluster: any) => {
        const childMarkers = cluster.getAllChildMarkers();
        let onlineCount = 0;
        let offlineCount = 0;

        childMarkers.forEach((marker: L.Marker) => {
            const markerLatLng = marker.getLatLng();
            const device = validDevices.find((d: Device) =>
                d.latitude === markerLatLng.lat && d.longitude === markerLatLng.lng
            );
            if (device) {
                device.online ? onlineCount++ : offlineCount++;
            }
        });

        return L.divIcon({
            html: `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;width:40px;height:40px;background:#fff;border:2px solid #4D4E65;border-radius:50%;position:relative;overflow:hidden;">
                <div style="position:absolute;top:0;left:0;right:0;bottom:50%;background:#5ABEA6;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;color:#fff;">${onlineCount}</div>
                <div style="position:absolute;top:50%;left:0;right:0;bottom:0;background:#e07777;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;color:#fff;">${offlineCount}</div>
                <div style="position:absolute;top:50%;left:0;right:0;height:2px;background:#4D4E65;transform:translateY(-50%);"></div>
            </div>`,
            className: 'custom-cluster-icon',
            iconSize: [40, 40],
            iconAnchor: [20, 20],
        });
    };

    if (!devices || devices.length === 0) {
        return (
            <div className="mapall-empty">
                <MapContainer
                    zoom={5}
                    center={[48.0196, 66.9237]}
                    className="mapall-container"
                    scrollWheelZoom={true}
                >
                    <TileLayer
                    // attribution={`&copy; <a href="${MAP_TILE_URL}">Local server map</a>`}
                    // url={`${MAP_TILE_URL}/{z}/{x}/{y}.png`}
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url={MAP_TILE_URL}
                        subdomains={['a', 'b', 'c']}
                    />
                </MapContainer>
            </div>
        );
    }

    if (validDevices.length === 0) {
        return (
            <div className="mapall-novalid">
                Нет устройств с допустимыми координатами для отображения
            </div>
        );
    }

    const DeviceMarker: React.FC<{
        device: Device;
        isSelected: boolean;
    }> = ({ device, isSelected }) => {
        if (!isPointInKazakhstan(device.latitude, device.longitude)) {
            return null;
        }

        const handleMarkerClick = () => {
            const map = mapRef.current;
            if (map) {
                const currentZoom = map.getZoom();
                if (currentZoom >= 14) {
                    setSelectedUID(device.UID);
                    setIsExternalSelection(false);
                    onSelectDeviceUID?.(device.UID);
                    setTimeout(() => {
                        const marker = markerRefs.current[device.UID];
                        if (marker && marker.openPopup) {
                            marker.openPopup();
                        }
                    }, 0);
                } else {
                    map.setView([device.latitude, device.longitude], 16, { animate: true });
                    setSelectedUID(device.UID);
                    setIsExternalSelection(false);
                    onSelectDeviceUID?.(device.UID);
                }
            }
        };

        return (
            <Marker
                key={device.UID}
                position={[device.latitude, device.longitude]}
                icon={createCameraIcon(device.online, isSelected, device.name)}
                eventHandlers={{ click: handleMarkerClick }}
                ref={ref => {
                    if (ref) {
                        markerRefs.current[device.UID] = ref;
                    } else {
                        delete markerRefs.current[device.UID];
                    }
                }}
            >
                <Popup>
                    <div style={{
                        backgroundColor: '#E8EBED',
                        height: '38px',
                        margin: '-17px -21px 0px -21px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 40px 0 20px',
                        borderTopLeftRadius: '12px',
                        borderTopRightRadius: '12px'

                    }}>
                        <style>{`
                                .leaflet-popup-content-wrapper {
                                    padding-top: 0 !important;
                                }
                                .leaflet-popup-close-button {
                                    font-size: 28px !important;
                                    right: 5px !important;
                                }
                                .leaflet-popup-content {
                                    margin: 13px 20px 13px 20px !important;
                                }
                            `}</style>
                        <span className="mapall-popup-name" style={{ fontSize: '16px', fontWeight: '400', color: '#575655', paddingRight: '30px' }}>{device.name}</span>
                    </div>
                    <div className="mapall-popup">
                        <p>
                            {device.description}
                        </p>
                        <p>
                            {device.model}
                        </p>
                        <p style={{ margin: '0' }}>
                            <span
                                className="mapall-popup-link"
                                onClick={() => {
                                    navigate(`/device/${device.DID}`);
                                }}
                                style={{
                                    display: 'block',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#fff',
                                    backgroundColor: '#4D4E65',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    textAlign: 'center',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}
                            >
                                Перейти
                            </span>
                        </p>
                    </div>
                </Popup>
            </Marker>
        );
    };

    return (
        <div className="mapall-root">
            <MapContainer
                ref={mapRef}
                zoom={10}
                className="mapall-container"
                scrollWheelZoom={true}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url={MAP_TILE_URL}
                    subdomains={['a', 'b', 'c']}
                />
                {selectedDevice ? (
                    <ZoomToDevice device={selectedDevice} />
                ) : (
                    bounds && <FitBounds bounds={bounds} />
                )}
                <MarkerClusterGroup
                    spiderfyDistanceMultiplier={6}
                    iconCreateFunction={createClusterIcon}
                >
                    {validDevices.map((device) => (
                        <DeviceMarker
                            key={device.UID}
                            device={device}
                            isSelected={device.UID === selectedUID}
                        />
                    ))}
                </MarkerClusterGroup>
            </MapContainer>
        </div>
    );
};

export default LocationMapAll; 